import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
} from "@mui/material";
import axios from "axios";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { Add, Delete, Edit, CloudUpload } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const PortfolioProjects = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [imagePath, setImagePath] = useState("");
  const [additionalImages, setAdditionalImages] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [highlightInput, setHighlightInput] = useState("");
  const [editingOrders, setEditingOrders] = useState({});

  const [formData, setFormData] = useState({ 
    title: "", 
    description: "", 
    image: "",
    additionalImages: [],
    url: "",
    category: "",
    client: "",
    location: "",
    completedDate: new Date(),
    challenge: "",
    overview: "",
    highlights: []
  });
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  
const handleOrderChange = (id, value) => {
  const order = parseInt(value, 10);
  setEditingOrders((prev) => ({ ...prev, [id]: value }));

  if (isNaN(order) || order < 1) return;
  
  // Optional: Debounce this or add a Save button
  updateOrder(id, order);
};
const updateOrder = async (id, order) => {
  try {
    await axios.put(`${apiUrl}/portfolio/projects/${id}/order`, {
      projectId: order,
    });

    setSnackbarOpen(true);
    setErrorMessage("Project order updated successfully");

    setEditingOrders((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });

    fetchData(); // Refresh UI
  } catch (error) {
    console.error("Failed to update project order:", error);
    setErrorMessage("Failed to update project order");
    setSnackbarOpen(true);
  }
};



  // Fetch projects and categories from the API
  const fetchData = async () => {
    try {
      const [projectsRes, categoriesRes] = await Promise.all([
        axios.get(`${apiUrl}/portfolio/projects?populate=category`),
        axios.get(`${apiUrl}/portfolio/categories`)
      ]);
      
      setProjects(projectsRes.data.data);
      setCategories(categoriesRes.data.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
      setErrorMessage("Error fetching data");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Image upload handler
  const handleImageUpload = async (file, setPathCallback) => {
    const formData = new FormData();
    formData.append("image", file);
  
    try {
      const response = await axios.post(`${apiUrl}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity
      });
      // setPathCallback(response.data.filePath);
      setPathCallback(response.data.url);
      return response.data.url;
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage(
        error.response?.data?.error || 
        "Error uploading image. Please try a smaller file or different format."
      );
      setSnackbarOpen(true);
      return null;
    }
  };

  const handleMainImageChange = (event) => {
    const selectedImage = event.target.files[0];
    if (selectedImage) {
      handleImageUpload(selectedImage, setImagePath);
    }
  };

  const handleAdditionalImagesChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const uploadedPaths = [];
      for (const file of files) {
        const path = await handleImageUpload(file, () => {});
        if (path) {
          uploadedPaths.push(path);
        }
      }
      setAdditionalImages(prev => [...prev, ...uploadedPaths]);
    }
  };

  // Open the form for adding or editing
const handleOpenModal = (project = null) => {
  setSelectedProject(project);

  if (project) {
    // If editing an existing project
    setFormData({ 
      title: project.title || "",
      description: project.description || "",
      url: project.url || "",
      category: project.category?._id || project.category || "",
      client: project.client || "",
      location: project.location || "",
      challenge: project.challenge || "",
      overview: project.overview || "",
      highlights: project.highlights || [],
      completedDate: new Date(project.completedDate) || new Date(),
      projectId: project.projectId || 1 // <-- Added this line
    });

    setImagePath(project.image || "");
    setAdditionalImages(project.additionalImages || []);
    setHighlights(project.highlights || []);
  } else {
    // If creating a new project
    setFormData({ 
      title: "", 
      description: "", 
      url: "",
      category: "",
      client: "",
      location: "",
      completedDate: new Date(),
      challenge: "",
      overview: "",
      highlights: [],
      
    });

    setImagePath("");
    setAdditionalImages([]);
    setHighlights([]);
  }

  setOpenModal(true);
  setErrors({});
};

  // Close the form
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedProject(null);
    setErrors({});
    setImagePath("");
    setAdditionalImages([]);
    setHighlights([]);
  };

  // Handle form data change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, completedDate: date });
  };

  const handleAddHighlight = () => {
    if (highlightInput.trim() && !highlights.includes(highlightInput.trim())) {
      setHighlights([...highlights, highlightInput.trim()]);
      setHighlightInput("");
    }
  };

  const handleRemoveHighlight = (index) => {
    const updated = [...highlights];
    updated.splice(index, 1);
    setHighlights(updated);
  };

  const handleRemoveAdditionalImage = (index) => {
    const updated = [...additionalImages];
    updated.splice(index, 1);
    setAdditionalImages(updated);
  };

  // Form validation function
  const validateForm = () => {
  const newErrors = {};

  // Validate only when adding
  if (!selectedProject) {
    if (!formData.title.trim()) {
      newErrors.title = "Project title is required.";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required.";
    }
    if (!formData.url.trim()) {
      newErrors.url = "URL is required.";
    }
    if (!formData.category) {
      newErrors.category = "Category is required.";
    }
  
    
 

    
    if (!imagePath) {
      newErrors.image = "Project image is required.";
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  // Add or update a project
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  try {
    const baseData = {
      ...formData,
      image: imagePath,
      additionalImages: additionalImages,
      highlights: highlights
    };

    if (selectedProject) {
      const { completedDate, ...updateData } = baseData;

      const response = await axios.put(
        `${apiUrl}/portfolio/projects/${selectedProject._id}`,
        updateData
      );

      const updatedProject = await axios.get(
        `${apiUrl}/portfolio/projects/${selectedProject._id}?populate=category`
      );

      setProjects(prevData =>
        prevData.map(item => 
          item._id === selectedProject._id 
            ? updatedProject.data.data 
            : item
        )
      );
    } else {
      const response = await axios.post(
        `${apiUrl}/portfolio/projects`,
        baseData
      );

      const categoryResponse = await axios.get(
        `${apiUrl}/portfolio/categories/${formData.category}`
      );

      const completeProject = {
        ...response.data.data,
        category: categoryResponse.data.data
      };

      setProjects(prevData => [...prevData, completeProject]);

      await axios.put(
        `${apiUrl}/portfolio/categories/${formData.category}`,
        { $push: { projects: response.data.data._id } }
      );
    }
    handleCloseModal();
  } catch (error) {
    console.error("Error saving project:", error);
    setErrorMessage("Error saving project.");
    setSnackbarOpen(true);
  }
};

  // Delete a project
  const handleDelete = async (id) => {
    try {
      // First get the project to know its category
      const projectResponse = await axios.get(`${apiUrl}/portfolio/projects/${id}`);
       
      const project = projectResponse.data.data;
      
      if (!project) {
        throw new Error("Project not found");
      }
  
      // Get the category ID (handles both populated and non-populated cases)
      const categoryId = project.category?._id || project.category;
      
      // Delete the project
      await axios.delete(`${apiUrl}/portfolio/projects/${id}`);
      
      // Remove project reference from category if category exists
      if (categoryId) {
        await axios.put(
          `${apiUrl}/portfolio/categories/${categoryId}`,
          { $pull: { projects: id } }
        );
      }
       await  fetchData();
      // Update local state
      setProjects(prevData => prevData.filter(item => item._id !== id));
    } catch (error) {
      console.error("Error deleting project:", error);
      setErrorMessage(error.response?.data?.message || "Error deleting project");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const columns = [
    { field: "title", headerName: "Project Title", flex: 0.2 },
    { 
      field: "image", 
      headerName: "Image", 
      flex: 0.1,
      renderCell: (params) => (
        <img 
          src={params.row.image} 
          alt="project" 
          style={{ width: 60, height: 40, objectFit: 'cover' }} 
        />
      )
    },
 {
  field: "projectId",
  headerName: "Order",
  flex: 0.1,
  renderCell: (params) => (
    <TextField
      type="number"
      size="small"
      variant="outlined"
      value={editingOrders[params.row._id] ?? params.row.projectId ?? ""}
      onChange={(e) => {
        const val = e.target.value;
        setEditingOrders((prev) => ({ ...prev, [params.row._id]: val }));
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          const val = parseInt(editingOrders[params.row._id], 10);
          if (!isNaN(val) && val >= 1) {
            updateOrder(params.row._id, val);
          }
        }
      }}
      inputProps={{ style: { width: 60, padding: "5px" } }}
    />
  ),
},



    { 
      field: "category", 
      headerName: "Category", 
      flex: 0.15,
      renderCell: (params) => {
        if (!params.row.category) return "Uncategorized";
        if (typeof params.row.category === 'object') return params.row.category.name;
        return "Uncategorized";
      }
    },
    { field: "client", headerName: "Client", flex: 0.15 },
    { field: "location", headerName: "Location", flex: 0.15 },
    { 
      field: "completedDate", 
      headerName: "Completed Date", 
      flex: 0.15,
      renderCell: (params) => new Date(params.row.completedDate).toLocaleDateString()
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.2,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleOpenModal(params.row)}
            startIcon={<Edit />}
            sx={{ mr: 1, backgroundColor: "#4d547d" }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleDelete(params.row._id)}
            startIcon={<Delete />}
            sx={{ backgroundColor: "white", color: "#191f45" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="PORTFOLIO PROJECTS" subtitle="Manage your portfolio projects" />
      
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => handleOpenModal(null)}
        startIcon={<Add />}
        sx={{ mb: 3 }}
      >
        Add New Project
      </Button>

      {/* Projects Cards View */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {projects.map((project) => (
          <Grid item xs={12} md={6} lg={4} key={project._id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              border: '1px solid #4d547d'
            }}>
              <CardMedia
                component="img"
                height="140"
                image={project.image}
                alt={project.title}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h2">
                  {project.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {project.description.substring(0, 100)}...
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Client:</strong> {project.client}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Location:</strong> {project.location}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Completed:</strong> {new Date(project.completedDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Category:</strong> {project.category?.name || "Uncategorized"}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {project.highlights?.map((highlight, index) => (
                    <Chip key={index} label={highlight} size="small" />
                  ))}
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => handleOpenModal(project)}
                  sx={{ color: "#4d547d" }}
                >
                  Edit
                </Button>
                <Button 
                  size="small" 
                  onClick={() => handleDelete(project._id)}
                  sx={{ color: "#ff4444" }}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Data Grid View */}
      <Box mt="40px" height="75vh">
        <DataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={projects || []}
          columns={columns}
          pageSize={5}
          sx={{ backgroundColor: "#191f45", color: "white" }}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
        />
      </Box>

      {/* Add/Edit Project Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle>{selectedProject ? "Edit Project" : "Add New Project"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                          
                  
                  <TextField
                margin="normal"
                label="Project Title"
                fullWidth
                variant="outlined"
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
              />

              <TextField
                margin="normal"
                label="Client"
                fullWidth
                variant="outlined"
                name="client"
                value={formData.client}
                onChange={handleChange}
                error={!!errors.client}
                helperText={errors.client}
              />

              <TextField
                margin="normal"
                label="Location"
                fullWidth
                variant="outlined"
                name="location"
                value={formData.location}
                onChange={handleChange}
                error={!!errors.location}
                helperText={errors.location}
              />

              {!selectedProject && (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <DatePicker
      label="Completed Date"
      value={formData.completedDate}
      onChange={handleDateChange}
      renderInput={(params) => (
        <TextField 
          {...params} 
          fullWidth 
          margin="normal" 
          error={!!errors.completedDate}
          helperText={errors.completedDate}
        />
      )}
    />
  </LocalizationProvider>
)}

              <TextField
                margin="normal"
                label="URL"
                fullWidth
                variant="outlined"
                name="url"
                value={formData.url}
                onChange={handleChange}
                error={!!errors.url}
                helperText={errors.url}
              />

              <FormControl fullWidth margin="normal" error={!!errors.category}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  name="category"
                  value={formData.category}
                  label="Category"
                  onChange={handleChange}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <Typography color="error" variant="body2">
                    {errors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Project Image
                </Typography>
                {errors.image && (
                  <Typography color="error" variant="body2">
                    {errors.image}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {imagePath && (
                    <img 
                      src={imagePath}
                      alt="project preview" 
                      style={{ 
                        width: '100%', 
                        maxHeight: 200, 
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }} 
                    />
                  )}
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUpload />}
                    sx={{ backgroundColor: "#4d547d" }}
                  >
                    Upload Main Image
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={handleMainImageChange} 
                    />
                  </Button>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Additional Images
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {additionalImages.map((img, index) => (
                      <Box key={index} sx={{ position: 'relative' }}>
                        <img 
                          src={img}
                          alt={`additional ${index}`}
                          style={{ 
                            width: 80, 
                            height: 60, 
                            objectFit: 'cover',
                            border: '1px solid #ddd',
                            borderRadius: 4
                          }} 
                        />
                        <IconButton
                          size="small"
                          sx={{ 
                            position: 'absolute', 
                            top: -8, 
                            right: -8, 
                            backgroundColor: 'error.main',
                            color: 'white',
                            '&:hover': { backgroundColor: 'error.dark' }
                          }}
                          onClick={() => handleRemoveAdditionalImage(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    sx={{ borderColor: "#4d547d", color: "#4d547d" }}
                  >
                    Upload Additional Images
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      multiple
                      onChange={handleAdditionalImagesChange} 
                    />
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="normal"
                label="Challenge"
                fullWidth
                variant="outlined"
                name="challenge"
                value={formData.challenge}
                onChange={handleChange}
                multiline
                rows={2}
                error={!!errors.challenge}
                helperText={errors.challenge}
              />

              <TextField
                margin="normal"
                label="Overview"
                fullWidth
                variant="outlined"
                name="overview"
                value={formData.overview}
                onChange={handleChange}
                multiline
                rows={3}
                error={!!errors.overview}
                helperText={errors.overview}
              />

              <TextField
                margin="normal"
                label="Description"
                fullWidth
                variant="outlined"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!errors.description}
                helperText={errors.description}
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Project Highlights
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    label="Add Highlight"
                    variant="outlined"
                    size="small"
                    value={highlightInput}
                    onChange={(e) => setHighlightInput(e.target.value)}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleAddHighlight}
                    sx={{ backgroundColor: "#4d547d" }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {highlights.map((highlight, index) => (
                    <Chip 
                      key={index} 
                      label={highlight} 
                      onDelete={() => handleRemoveHighlight(index)}
                      sx={{ backgroundColor: '#4d547d', color: 'white' }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ backgroundColor: "#4d547d" }}
          >
            {selectedProject ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PortfolioProjects;